import { useMemo, useState } from 'react'
import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { SkillsHeader, SkillsToolbar, SkillCategorySection } from '@/components/projects'
import type { SkillsViewMode } from '@/components/projects'
import { getErrorMessage } from '@/api/errors'
import { useAuth, useDebounce, useSkills } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { Skill, SkillCategory } from '@/types'
import { SKILL_CATEGORIES, skillCategory } from '@/types'

type SectionState = Record<SkillCategory, boolean>

const EMPTY_TEXT: Record<SkillCategory, { none: string; noMatch: string }> = {
  System: { none: 'No system skills available.', noMatch: 'No system skills match your search.' },
  Custom: { none: 'No custom skills yet. Create one to give your agents reusable know-how.', noMatch: 'No custom skills match your search.' },
}

function readSessionViewMode(projectId: string): SkillsViewMode {
  try {
    const stored = sessionStorage.getItem(`skills-view-mode-${projectId}`)
    if (stored === 'list' || stored === 'grid') return stored
  } catch { /* ignore */ }
  return 'grid'
}

function readSessionSearch(projectId: string): string {
  try {
    return sessionStorage.getItem(`skills-search-${projectId}`) ?? ''
  } catch { /* ignore */ }
  return ''
}

function readSessionSections(projectId: string): SectionState {
  const state: SectionState = { System: true, Custom: true }
  try {
    const stored = sessionStorage.getItem(`skills-sections-${projectId}`)
    const parsed: unknown = stored ? JSON.parse(stored) : null
    if (parsed && typeof parsed === 'object') {
      for (const category of SKILL_CATEGORIES) {
        const value = (parsed as Record<string, unknown>)[category]
        if (typeof value === 'boolean') state[category] = value
      }
    }
  } catch { /* ignore */ }
  return state
}

function matchesSearch(skill: Skill, term: string): boolean {
  if (!term) return true
  return (
    skill.displayName.toLowerCase().includes(term) ||
    skill.name.toLowerCase().includes(term) ||
    (skill.description?.toLowerCase().includes(term) ?? false) ||
    (skill.latestVersion?.toLowerCase().includes(term) ?? false)
  )
}

function groupByCategory(skills: Skill[]): Record<SkillCategory, Skill[]> {
  const groups: Record<SkillCategory, Skill[]> = { System: [], Custom: [] }
  for (const skill of skills) groups[skillCategory(skill)].push(skill)
  return groups
}

export function SkillsPage() {
  const { projectId } = useParams()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)

  if (authLoading || !projectId) {
    return null
  }

  if (!hasPermission(Permissions.Project.Skill.Read, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  return <SkillsPageContent key={projectId} projectId={projectId} />
}

function SkillsPageContent({ projectId }: { projectId: string }) {
  const navigate = useNavigate()
  const { data: skills = [], isLoading, isError, error } = useSkills(projectId)
  const [search, setSearch] = useState(() => readSessionSearch(projectId))
  const debouncedSearch = useDebounce(search)
  const [viewMode, setViewMode] = useState<SkillsViewMode>(() => readSessionViewMode(projectId))
  const [sectionExpanded, setSectionExpanded] = useState<SectionState>(() => readSessionSections(projectId))

  const term = debouncedSearch.trim().toLowerCase()
  const allByCategory = useMemo(() => groupByCategory(skills), [skills])
  const filtered = useMemo(() => skills.filter(skill => matchesSearch(skill, term)), [skills, term])
  const byCategory = useMemo(() => groupByCategory(filtered), [filtered])

  const persistViewMode = (mode: SkillsViewMode) => {
    try { sessionStorage.setItem(`skills-view-mode-${projectId}`, mode) } catch { /* ignore */ }
  }

  const persistSections = (state: SectionState) => {
    try { sessionStorage.setItem(`skills-sections-${projectId}`, JSON.stringify(state)) } catch { /* ignore */ }
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    try { sessionStorage.setItem(`skills-search-${projectId}`, value) } catch { /* ignore */ }
  }

  const handleViewModeChange = (mode: SkillsViewMode) => {
    setViewMode(mode)
    persistViewMode(mode)
  }

  const updateSections = (next: SectionState) => {
    setSectionExpanded(next)
    persistSections(next)
  }

  const handleToggleSection = (category: SkillCategory) =>
    updateSections({ ...sectionExpanded, [category]: !sectionExpanded[category] })

  const noMatches = term.length > 0 && skills.length > 0 && filtered.length === 0

  const emptyTextFor = (category: SkillCategory) =>
    term && allByCategory[category].length > 0 ? EMPTY_TEXT[category].noMatch : EMPTY_TEXT[category].none

  let body
  if (isLoading) {
    body = <p className="py-4 text-sm text-muted-foreground" role="status" aria-live="polite">Loading skills…</p>
  } else if (isError) {
    body = (
      <div className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-destructive" role="alert">
        {getErrorMessage(error) ?? "Couldn't load skills. Refresh to try again."}
      </div>
    )
  } else if (noMatches) {
    body = (
      <div className="py-12 text-center text-sm text-muted-foreground" role="status" data-testid="skills-no-results">
        No skills match “{debouncedSearch.trim()}”. Try a different name, description or version.
      </div>
    )
  } else {
    body = (
      <div className="space-y-2">
        {SKILL_CATEGORIES.map(category => (
          <SkillCategorySection
            key={category}
            projectId={projectId}
            category={category}
            skills={byCategory[category]}
            viewMode={viewMode}
            isExpanded={sectionExpanded[category]}
            onToggle={() => handleToggleSection(category)}
            emptyText={emptyTextFor(category)}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="skills-page">
      <SkillsHeader projectId={projectId} onCreateSkill={() => navigate(`/projects/${projectId}/workflows/skills/new/config`)} />
      <SkillsToolbar search={search} onSearchChange={handleSearchChange} viewMode={viewMode} onViewModeChange={handleViewModeChange} />
      {body}
    </div>
  )
}
